/** ============================================================================
 * utils
 *
 * @version   1.0.1
 * @author    RIESTER <wordpress@riester.com>
 * @copyright 2022 RIESTER
 * ========================================================================== */
import Cookies from 'js-cookie';
import { mq } from 'verge';

/**
 * Check if object has a key or path using dot notation
 *
 * @export
 * @param {Object} obj The object to check
 * @param {string} path The path to check for
 * @returns {boolean}
 */
export function has(obj, path) {
	// Regex explained: https://regexr.com/58j0k
	const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);

	return !!pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj);
}

/**
 * Check if user has reduced motion setting turned on
 *
 * @type {boolean}
 */
export const isReduced =
	window.matchMedia('(prefers-reduced-motion: reduce)') === true ||
	window.matchMedia('(prefers-reduced-motion: reduce)').matches === true;

/**
 * Programatically fire an event
 *
 * @export
 * @param {Element} el Element the event is attached to
 * @param {string} etype Type of event
 */
export function eventFire(el, etype) {
	if (el.fireEvent) {
		el.fireEvent('on' + etype);
	} else {
		var evObj = document.createEvent('Events');
		evObj.initEvent(etype, true, false);
		el.dispatchEvent(evObj);
	}
}

/**
 * Set css property on element
 *
 * @export
 * @param {string|HTMLElement[]} el The element to add the style to
 * @param {Object} style An object with key/pair values for property and value
 */
export function css(el, style) {
	var matches = el;
	if (typeof el === 'string') {
		matches = document.querySelectorAll(el);
	}
	if (matches.length > 0) {
		Array.from(matches).forEach((e) => {
			for (const property in style) e.style[property] = style[property];
		});
	}
}

/**
 * Programatically add event listeners
 *
 * @export
 * @param {Element} el                        Element to attach to
 * @param {string} etype                Event type
 * @param {function} fn                 Function to run
 * @param {boolean} [nobubble=false]    Add stopPropagation
 * @param {boolean} [stopdefault=false] Add preventDefault
 * @param {Object} [opt={}]             Listener options
 */
export function listen(el, etype, fn, nobubble = false, stopdefault = false, opt = {}) {
	var fnwrap = function (e) {
		e = e || event;
		if (nobubble) {
			noBubbles(e);
		}
		if (stopdefault) {
			noDefault(e);
		}
		return fn.apply(el, Array.prototype.slice.call(arguments));
	};
	if (el.attachEvent) {
		el.attachEvent('on' + etype, fnwrap);
	} else {
		el.addEventListener(etype, fnwrap, opt);
	}
}

/**
 * Programatically add preventDefault function
 *
 * @export
 * @param {Event} e
 */
export function noDefault(e) {
	if (e.preventDefault) {
		e.preventDefault();
	} else {
		e.returnValue = false;
	}
}

/**
 * Programatically add stopPropagation function
 *
 * @export
 * @param {Event} e
 */
export function noBubbles(e) {
	if (e.stopPropagation) {
		e.stopPropagation();
	} else {
		e.cancelBubble = true;
	}
}

/**
 * Merge two objects
 *
 * @export
 * @returns {*}
 */
export function extend() {
	var obj,
		name,
		copy,
		target = arguments[0] || {},
		i = 1,
		length = arguments.length;

	for (; i < length; i++) {
		if ((obj = arguments[i]) !== null) {
			for (name in obj) {
				copy = obj[name];

				if (target === copy) {
					continue;
				} else if (copy !== undefined) {
					target[name] = copy;
				}
			}
		}
	}

	return target;
}

/**
 * Get the value of a key from an object or use the provided default value
 *
 * @export
 * @param {Object} obj The object to pull from
 * @param {string} path The path
 * @param {*} defValue The default value
 * @returns {*}
 */
export function get(obj, path, defValue) {
	// If path is not defined or it has false value
	if (!path) return undefined;
	// Check if path is string or array. Regex : ensure that we do not have '.' and brackets.
	// Regex explained: https://regexr.com/58j0k
	const pathArray = Array.isArray(path) ? path : path.match(/([^[.\]])+/g);
	// Find value
	const result = pathArray.reduce((prevObj, key) => prevObj && prevObj[key], obj);
	// If found value is undefined return default value; otherwise return the value
	return result === undefined ? defValue : result;
}

/**
 * Check if a variable is empty
 *
 * @export
 * @param {*} el The variable to check
 * @return {boolean} True if empty, false if not
 */
export function isEmpty(el) {
	if (el === undefined || el == null) {
		return true;
	}
	if (typeof el === 'string' && el.length > 0) {
		return false;
	} else if (el === true) {
		return false;
	} else if (el instanceof Object) {
		if (Array.isArray(el) && el.length > 0) {
			return false;
		} else {
			if (Object.keys(el).length > 0) {
				return false;
			}
		}
	}
	return false;
}

/**
 * Delete multiple keys from an object
 *
 * @export
 * @param {Object} obj
 * @param {Array} props
 * @returns {Object}
 */
export function omit(obj, props) {
	// eslint-disable-next-line
	obj = { ...obj };
	props.forEach((prop) => delete obj[prop]);
	return obj;
}

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
export function getTag(value) {
	if (value == null) {
		return value === undefined ? '[object Undefined]' : '[object Null]';
	}
	return Object.prototype.toString.call(value);
}

/**
 * Function for making strings camelCase
 *
 * @param {string} str The string to convert
 * @return {string} The converted string
 */
export function camelCase(str) {
	return `${str.charAt(0).toLowerCase()}${str
		.replace(/[\W_]/g, '|')
		.split('|')
		.map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1)}`)
		.join('')
		.slice(1)}`;
}

/**
 * Removes empty attributes (unless they're boolean attributes or data attributes)
 *
 * @link https://meiert.com/en/blog/boolean-attributes-of-html/
 *
 * @param {*} [args={}]
 * @param {boolean} [removeEmpty=true]
 * @return {*}
 */
function filterEmptyAtts(args = {}, removeEmpty = true) {
	let booleanAtts = [
		'allowfullscreen',
		'allowpaymentrequest',
		'async',
		'autofocus',
		'autoplay',
		'checked',
		'controls',
		'default',
		'defer',
		'disabled',
		'formnovalidate',
		'hidden',
		'ismap',
		'itemscope',
		'loop',
		'multiple',
		'muted',
		'nomodule',
		'novalidate',
		'open',
		'playsinline',
		'readonly',
		'required',
		'reversed',
		'selected',
		'truespeed',
	];

	if (removeEmpty) {
		for (const prop in args) {
			if (Object.hasOwnProperty.call(args, prop)) {
				const value = args[prop];

				inArray(booleanAtts, prop);
				let isBooleanAttr = inArray(booleanAtts, prop) != -1,
					isDataAttr = prop.startsWith('data-'),
					isEmptyAttr = isEmpty(value);

				if ((!isBooleanAttr || !isDataAttr) && isEmptyAttr) {
					delete args[prop];
				}
			}
		}
	}

	return args;
}

export function outputAttributes(atts = {}, filter = true) {
	atts = filterEmptyAtts(atts, filter);

	let args = [];

	for (const prop in atts) {
		if (Object.hasOwnProperty.call(atts, prop)) {
			const value = atts[prop];

			if (isEmpty(value)) {
				args.push(prop);
			} else if (typeof value == 'object') {
				args.push(`${prop}='${JSON.stringify(value)}'`);
			} else {
				args.push(`${prop}="${value}"`);
			}
		}
	}

	return args.join(' ');
}

/**
 * Description placeholder
 * @date 3/9/2023 - 3:27:14 PM
 *
 * @export
 * @param {string|HTMLElement} el
 * @returns {Object} Object of attributes
 */
export function allAttributes(el) {
	if (typeof el == 'string') {
		el = stringToHtml(el);
	}
	let attributes = {};
	for (let i = 0, l = el.attributes.length; i < l; ++i) {
		const nodeName = el.attributes.item(i).nodeName;
		const nodeValue = el.attributes.item(i).nodeValue;

		attributes[nodeName] = nodeValue;
	}

	return attributes;
}

/**
 *	Change the tag of a node element
 *
 * @param  {Element}  original  The element to change
 * @param  {string}   tag        The new tag
 *
 * @return {Element} The updated element
 */
export function changeTag(original, tag) {
	const replacement = document.createElement(tag);

	// Grab all of the original's attributes, and pass them to the replacement
	for (let i = 0, l = original.attributes.length; i < l; ++i) {
		const nodeName = original.attributes.item(i).nodeName;
		const nodeValue = original.attributes.item(i).nodeValue;

		replacement.setAttribute(nodeName, nodeValue);
	}

	// Persist contents
	replacement.innerHTML = original.innerHTML;

	// Switch!
	original.parentNode.replaceChild(replacement, original);

	return original;
}

/**
 * Check for window.DOMParser support
 *
 * @type {boolean}
 */
const domParserSupport = (function () {
	if (!window.DOMParser) return false;
	var parser = new DOMParser();
	try {
		parser.parseFromString('x', 'text/html');
	} catch (err) {
		return false;
	}
	return true;
})();

/**
 * Convert a template string into HTML DOM nodes
 * @param  {String} str The template string
 * @return {Node}       The template HTML
 */
export function stringToHtml(str) {
	// If DOMParser is supported, use it
	if (domParserSupport) {
		var parser = new DOMParser();
		var doc = parser.parseFromString(str, 'text/html');
		return doc.body.firstElementChild;
	}

	// Otherwise, fallback to old-school method
	var dom = document.createElement('div');
	dom.innerHTML = str;
	return dom;
}

/**
 * Checks if a variable is an instance of a DOM node
 *
 * @link https://stackoverflow.com/questions/384286/how-do-you-check-if-a-javascript-object-is-a-dom-object
 * @export
 * @param {*} o
 * @return {boolean} Returns true if it is a DOM element
 */
export function isNode(o) {
	return typeof Node === 'object'
		? o instanceof Node
		: o && typeof o === 'object' && typeof o.nodeType === 'number' && typeof o.nodeName === 'string';
}

/**
 * Checks if a variable is an instance of HTMLElement
 *
 * @export
 * @param {*} o
 * @returns {boolean} Returns true if it is an HTMLElement
 */
export function isElement(o) {
	return typeof HTMLElement === 'object'
		? o instanceof HTMLElement //DOM2
		: o && typeof o === 'object' && o !== null && o.nodeType === 1 && typeof o.nodeName === 'string';
}
/**
 * IE safe way of testing if an object is a NodeList
 *
 * @link https://stackoverflow.com/questions/151348/how-to-check-if-an-object-is-an-instance-of-a-nodelist-in-ie
 * @param {*} el
 * @return {boolean} Returns true if it is a NodeList
 */
function isNodeList(el) {
	if (
		typeof el.length === 'number' &&
		typeof el.item !== 'undefined' &&
		typeof el.entries === 'function' &&
		typeof el.forEach === 'function' &&
		typeof el.keys === 'function' &&
		typeof el.values === 'function'
	) {
		if (isElement(el[0])) {
			return true;
		} else {
			return false;
		}
	}
	return false;
}

/**
 * jQuery free way to wrap an element
 *
 * @export
 * @param {Element} toWrap
 * @param {string} wrapper
 */
export function wrapElement(toWrap, wrapper) {
	wrapper = stringToHtml(wrapper);

	var parent;

	//toWrap.parentNode.appendChild(wrapper);

	if (isNodeList(toWrap) && toWrap.length > 0) {
		parent = toWrap[0].parentNode;
		parent.insertBefore(wrapper, toWrap[0]);
		toWrap.forEach((item) => {
			wrapper.appendChild(item);
		});
	} else {
		parent = toWrap.parentNode;
		parent.insertBefore(wrapper, toWrap);
		wrapper.appendChild(toWrap);
	}

	// return wrapper;
}

/**
 * jQuery-free way to unwrap and element
 *
 * @export
 * @param {Element} el
 */
export function unwrapElement(el) {
	// get the element's parent node
	var parent = el.parentNode;

	// move all children out of the element
	while (el.firstChild) parent.insertBefore(el.firstChild, el);

	// remove the empty element
	parent.removeChild(el);
}

/**
 *
 *
 * @export
 * @param {*} [timeToAdd={}]
 * @return {*}
 */
export function addTime(timeToAdd = {}) {
	let { hours, days, weeks, months, years } = timeToAdd;

	if (!isEmpty(hours)) {
		hours = parseFloat(hours) * 60 * 60 * 1000;
	} else {
		hours = 0;
	}
	if (!isEmpty(days)) {
		days = parseFloat(days) * 24 * 60 * 60 * 1000;
	} else {
		days = 0;
	}

	if (!isEmpty(weeks)) {
		weeks = parseFloat(weeks) * 7 * 24 * 60 * 60 * 1000;
	} else {
		weeks = 0;
	}
	if (!isEmpty(months)) {
		months = parseFloat(months) * 4 * 7 * 24 * 60 * 60 * 1000;
	} else {
		months = 0;
	}
	if (!isEmpty(years)) {
		years = parseFloat(years) * 12 * 4 * 7 * 24 * 60 * 60 * 1000;
	} else {
		years = 0;
	}

	var time = hours + days + weeks + months + years;

	return time;
}

/**
 * Delete a cookie
 *
 * @export
 * @param {string} [key='']
 * @param {string} [namespace='MFB_']
 */
export function removeCookie(key = '', namespace = 'MFB_') {
	Cookies.remove(`${namespace}${key}`);
}

/**
 * Set a cookie
 *
 * @export
 * @param {string} [key='']
 * @param {string} [value='']
 * @param {{}} [expires={}]
 * @param {{}} [options={}]
 * @param {string} [namespace='MFB_']
 */
export function setCookie(key = '', value = '', expires = {}, options = {}, namespace = 'MFB_') {
	var timeToAdd = addTime(expires);
	var date = new Date();
	var expiryTime = parseInt(date.getTime()) + timeToAdd;
	date.setTime(expiryTime);
	// var utcTime = date.toUTCString();
	options = extend(
		{
			expires: date,
			path: '/',
			secure: 'https:' == document.location.protocol,
		},
		options
	);
	Cookies.set(`${namespace}${key}`, value, options);
}

/**
 * Get a cookie
 *
 * @export
 * @param {*} key
 * @param {*} [defaultVal=null]
 * @param {string} [namespace='MFB_']
 * @returns {*}
 */
export function getCookie(key, defaultVal = null, namespace = 'MFB_') {
	let cookie = Cookies.get(`${namespace}${key}`);
	if (isEmpty(cookie)) {
		cookie = defaultVal;
	}
	return cookie;
}

/**
 * Adds focus class for better accessibility
 *
 * @export
 * @param {*} event
 * @param {string} [parentClass='nav']
 */
export function toggleFocus(event, parentClass = 'nav') {
	if (event.type === 'focus' || event.type === 'blur') {
		let self = event.target;

		if (!isEmpty(self)) {
			const elementClasses = self.classList;

			if (!isEmpty(elementClasses)) {
				// Move up through the ancestors of the current link until we hit .nav-menu.
				while (!elementClasses.contains(parentClass)) {
					// On li elements toggle the class .focus.
					if ('li' === self.tagName.toLowerCase()) {
						self.classList.toggle('focus');
					}
					self = self.parentNode;
				}
			}
		}
	}

	if (event.type === 'touchstart') {
		const menuItem = self.parentNode;
		event.preventDefault();
		for (const link of menuItem.parentNode.children) {
			if (menuItem !== link) {
				link.classList.remove('focus');
			}
		}
		menuItem.classList.toggle('focus');
	}
}

// URL updates and the element focus is maintained
// originally found via in Update 3 on http://www.learningjquery.com/2007/10/improved-animated-scrolling-script-for-samex-page-links

// filter handling for a /dir/ OR /indexordefault.page
/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:01 PM
 *
 * @export
 * @param {*} string
 * @returns {*}
 */
export function filterPath(string) {
	return string
		.replace(/^\//, '')
		.replace(/(index|default).[a-zA-Z]{3,4}$/, '')
		.replace(/\/$/, '');
}

/**
 * Get hash value for any string
 *
 * @param {string} str the string to extract from
 * @return {string|false} the hash or false
 */
export function getHash(str) {
	var index = str.indexOf('#');
	if (index !== -1) {
		return str.substring(index + 1);
	}
	return false;
}

/**
 * Get tallest element
 *
 * @param {string} el
 * @return {number|false}
 */
export function getTallest(el) {
	const elements = document.querySelectorAll(el);
	const matches = Array.from(elements);
	if (matches.length > 1) {
		const heights = matches.map(function (elem) {
			return elem.offsetHeight;
		});

		return Math.max.apply(null, heights);
	}
	return false;
}

/**
 * Make all elements match the tallest element
 *
 * @param {string} [elem='']
 * @param {*} [container=Document]
 */
export function matchHeights(elem = '', breakpoint = null) {
	var matches = document.querySelectorAll(elem);

	if (matches.length > 1) {
		if ((!isEmpty(breakpoint) && isBootstrapBP(breakpoint)) || isEmpty(breakpoint)) {
			var minHeight = getTallest(elem);

			if (false !== minHeight) {
				minHeight += 'px';
				matches.forEach(function (el) {
					el.style.minHeight = minHeight;
				});
			}
		} else {
			matches.forEach(function (el) {
				el.style.removeProperty('minHeight');
			});
		}

		window.addEventListener('resize', function () {
			matchHeights(elem);
		});
	}
}

/**
 * Get widest element
 *
 * @param {string|NodeList} el
 * @return {number}
 */
export function getWidest(el) {
	if (typeof el === 'string') {
		el = document.querySelectorAll(el);
	}
	const matches = Array.from(el);
	if (matches.length > 1) {
		const widths = matches.map(function (elem) {
			return elem.offsetWidth;
		});

		return Math.max.apply(null, widths);
	}
	return false;
}

/**
 * The function to run when the window is in a certain media query
 *
 * @callback windowResizeCallback
 */

/**
 * The function to run when the window is not in a certain media query
 *
 * @callback windowResizeReset
 */

/**
 * Description placeholder
 * @date 3/7/2023 - 6:27:56 PM
 *
 * @export
 * @param {windowResizeCallback} fn
 * @param {?windowResizeReset} [reset=null]
 * @param {?string} [breakpointMin=null]
 * @param {?string} [breakpointMax=null]
 */
export function resizeListener(fn, reset = null, breakpointMin = null, breakpointMax = null) {
	const mediaQuery = bsMediaQuery(breakpointMin, breakpointMax);

	if (!isEmpty(mediaQuery)) {
		const mql = window.matchMedia(mediaQuery);

		let cb = (e) => {
			if (e.matches) {
				fn();
			} else {
				if (typeof reset == 'function') {
					reset();
				}
			}
		};

		mql.addEventListener('change', cb);

		cb(mql);
	}
}

/**
 * Make all elements match the tallest element
 *
 * @param {string} [elem='']
 */
export function matchWidths(elem = '', breakpoint = null) {
	var matches = elem;
	if (typeof elem === 'string') {
		matches = document.querySelectorAll(matches);
	}
	if (matches.length > 1) {
		if ((!isEmpty(breakpoint) && isBootstrapBP(breakpoint)) || isEmpty(breakpoint)) {
			var minWidth = getWidest(elem);

			if (false !== minWidth) {
				minWidth += 'px';
				matches.forEach(function (el) {
					el.style.minWidth = minWidth;
				});
			}
		} else {
			matches.forEach(function (el) {
				el.style.removeProperty('minWidth');
			});
		}

		window.addEventListener('resize', function () {
			matchHeights(elem);
		});
	}
}

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:01 PM
 *
 * @param {*} styleSheet
 * @returns {boolean}
 */
const isSameDomain = (styleSheet) => {
	if (!styleSheet.href) {
		return true;
	}

	return styleSheet.href.indexOf(window.location.origin) === 0;
};

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:01 PM
 *
 * @param {*} rule
 * @returns {boolean}
 */
const isStyleRule = (rule) => rule.type === 1;

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:01 PM
 *
 * @param {string} [index='--']
 * @returns {*}
 */
const getCSSCustomPropIndex = (index = '--') =>
	[...document.styleSheets].filter(isSameDomain).reduce(
		(finalArr, sheet) =>
			finalArr.concat(
				[...sheet.cssRules].filter(isStyleRule).reduce((propValArr, rule) => {
					const props = [...rule.style]
						.map((propName) => [propName.trim(), rule.style.getPropertyValue(propName).trim()])
						.filter(([propName]) => propName.indexOf(index) === 0);
					return [...propValArr, ...props];
				}, [])
			),
		[]
	);

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:01 PM
 *
 * @export
 * @returns {*}
 */
export function bsAtts() {
	let props = getCSSCustomPropIndex('--bs-');

	props = Object.fromEntries(props);

	props = sortObjectByKeys(props);

	return props;
}

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @export
 * @param {string} [v='']
 * @returns {*}
 */
export function getBootstrapVar(v = '') {
	let props = bsAtts();

	v = `--bs-${v}`;

	try {
		if (has(props, v)) {
			return get(props, v);
		} else {
			throw new Error(`${v} does not exist`);
		}
	} catch (error) {
		console.error(error);
	}
}

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @export
 * @param {*} breakpoint
 * @returns {*}
 */
export function getBootstrapBP(breakpoint) {
	breakpoint = `bp-${breakpoint}`;
	return getBootstrapVar(breakpoint);
}

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @export
 * @param {*} breakpoint
 * @param {string} [type='min-width']
 * @returns {*}
 */
export function isBootstrapBP(breakpoint, type = 'min-width') {
	breakpoint = getBootstrapBP(breakpoint);

	let mqTest = mq(`(${type}: ${breakpoint})`);

	return mqTest;
}

/**
 * Description placeholder
 * @date 3/7/2023 - 6:39:14 PM
 *
 * @export
 * @param {?string} [minBp=null]
 * @param {?string} [maxBp=null]
 * @returns {string} the full media query
 */
export function bsMediaQuery(minBp = null, maxBp = null) {
	const breakpoints = bootstrapBPS();

	let mediaQuery = [];
	if (!isEmpty(minBp)) {
		mediaQuery.push(`(min-width: ${get(breakpoints, minBp)}px)`);
	}

	if (!isEmpty(maxBp)) {
		mediaQuery.push(`(max-width: ${get(breakpoints, maxBp)}px)`);
	}

	return mediaQuery.join(' and ');
}

/**
 * Get Bootstrap breakpoint variables
 *
 * @export
 * @returns {{}}
 */
export function bootstrapBPS() {
	let props = getCSSCustomPropIndex('--bs-bp-');

	props = Object.fromEntries(props);
	let sortable = [];
	for (var breakpoint in props) {
		sortable.push([breakpoint, parseFloat(props[breakpoint])]);
	}

	sortable.sort(function (a, b) {
		return a[1] - b[1];
	});

	let objSorted = {};
	sortable.forEach(function (item) {
		objSorted[item[0].replace('--bs-bp-', '')] = item[1];
	});

	return objSorted;
}

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @param {*} arr1
 * @param {*} arr2
 * @returns {*}
 */
export const inArray = (arr1, arr2) => {
	return arr1.some((r) => arr2.includes(r));
};

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @param {*} elem
 * @returns {{}}
 */
export const getParents = (elem) => {
	// Set up a parent array
	var parents = [];

	// Push each parent element to the array
	for (; elem && elem !== document; elem = elem.parentNode) {
		parents.push(elem);
	}

	// Return our parent array
	return parents;
};

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @param {*} el
 * @param {*} selector
 * @returns {{}}
 */
export const getParentsUntil = (el, selector) => {
	let parents = [],
		_el = el.parentNode;
	while (_el && typeof _el.matches === 'function') {
		parents.unshift(_el);
		if (_el.matches(selector)) return parents;
		else _el = _el.parentNode;
	}
	return [];
};

/**
 * @description determine if an array contains one or more items from another array.
 * @param {array} haystack the array to search.
 * @param {array} arr the array providing items to check for in the haystack.
 * @return {boolean} true|false if haystack contains at least one item from arr.
 */
export const findOne = (haystack, arr) => {
	return arr.some((v) => haystack.includes(v));
};

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @param {*} elem
 * @param {*} selector
 * @returns {*}
 */
export const childrenMatches = function (elem, selector) {
	return Array.prototype.filter.call(elem.children, function (child) {
		return child.matches(selector);
	});
};

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @export
 * @returns {boolean}
 */
export function currentBootstrapBP() {
	const breakpoints = bootstrapBPS();

	let keys = Object.keys(breakpoints);
	let values = Object.values(breakpoints);

	let current = false;

	for (let i = 0; i < keys.length; i++) {
		let key = keys[i];

		let val = values[i];

		let nextVal = values[i + 1];

		if (nextVal != undefined) {
			if (mq(`(min-width: ${val}px) and (max-width: ${nextVal}px)`)) {
				current = key;
			} else {
				continue;
			}
		} else {
			if (mq(`(min-width: ${val}px)`)) {
				current = key;
			} else {
				continue;
			}
		}
	}

	return current;
}

/**
 * Description placeholder
 * @date 3/7/2023 - 4:07:00 PM
 *
 * @export
 * @param {*} o
 * @returns {*}
 */
export function sortObjectByKeys(o) {
	return Object.keys(o)
		.sort()
		.reduce((r, k) => ((r[k] = o[k]), r), {});
}

/**
 * Get token
 *
 * @export
 */
export function getAjaxToken() {
	jQuery.ajax({
		url: 'https://midfirst.com/api/Token/get',
		type: 'GET',
		success: (data) => {
			return data.token;
		},
		error: (err) => {
			console.error(err);
		},
	});
}
